import React, { useEffect, useReducer, useState } from 'react';
import client from '../../helpers/Client';
import { DeleteIcon, AddIcon } from '@chakra-ui/icons'
import {
  Avatar,
  Box,
  Button,
  IconButton,
  useToast,
  Text,
  VStack,
  HStack,
  Icon,
  Textarea,
  Tooltip
} from '@chakra-ui/react'
import { Card, CardHeader, CardTitle, CardContent } from "../../components/Card";
import urlToId from '../../helpers/UrlToId';
import getFullName from "../../helpers/FullName";
import { FiMessageSquare, FiTrash2, FiUser, FiClock } from 'react-icons/fi';

function noteCreatedReducer(state) { return state + 1 }

function Note({ note, dispatchNoteCreated, isLast }) {
  const full_name = getFullName(note.created_by);
  const toast = useToast();
  const cardBg = "rgba(255, 255, 255, 0.8)";
  const borderColor = "gray.200";
  const textColor = "gray.600";
  const noteTextColor = "gray.800";
  const [deleting, setDeleting] = useState(false);

  const handleDelete = () => {
    const id = urlToId(note.url);
    if (!id) {
      toast({ title: 'Not silinemedi', description: 'Geçersiz not kimliği', status: 'error' });
      return;
    }
    const url = '/notes/' + id + '/delete/';
    setDeleting(true);
    client.post(url, { confirm: true })
      .then(() => {
        toast({ title: 'Not silindi', status: 'success' });
        dispatchNoteCreated();
      })
      .catch((error) => {
        const status = error?.response?.status;
        const detail = error?.response?.data?.detail || error?.message || 'Bilinmeyen hata';
        if (status === 403) {
          toast({
            title: 'Yetki yok',
            description: 'Başkasına ait notu silmeye yetkiniz yok.',
            status: 'error',
            duration: 5000,
            isClosable: true,
          });
        } else if (status === 401) {
          toast({
            title: 'Giriş gerekli',
            description: 'Lütfen oturum açıp tekrar deneyin.',
            status: 'error',
          });
        } else if (status === 404) {
          toast({
            title: 'Not bulunamadı',
            description: 'Not zaten silinmiş olabilir.',
            status: 'warning',
          });
          dispatchNoteCreated();
        } else {
          toast({
            title: 'Silme başarısız',
            description: String(detail),
            status: 'error',
          });
        }
      })
      .finally(() => setDeleting(false));
  };

  return (
    <Box
      bg={cardBg}
      backdropFilter="blur(20px)"
      border="1px solid"
      borderColor={borderColor}
      borderRadius="lg"
      p={4}
      mb={3}
      transition="all 0.3s ease"
      _hover={{
        transform: "translateY(-1px)",
        boxShadow: "md",
        borderColor: "blue.300",
      }}
    >
      <VStack spacing={3} align="stretch">
        {/* Header */}
        <HStack justify="space-between" align="center">
          <HStack spacing={3}>
            <Avatar
              size="sm"
              name={full_name}
              src={note.created_by.employee?.avatar}
              bg="blue.500"
              color="white"
            />
            <VStack spacing={0} align="start">
              <Text fontSize="sm" fontWeight="semibold" color="blue.600">
                {full_name}
              </Text>
              <Text fontSize="xs" color={textColor}>
                @{note.created_by.username}
              </Text>
            </VStack>
          </HStack>

          <HStack spacing={2}>


          </HStack>
        </HStack>

        {/* Note Content */}
        <Box
          bg={"gray.50"}
          borderRadius="md"
          p={3}
          border="1px solid"
          borderColor={"gray.100"}
        >
          <Text fontSize="sm" color={noteTextColor} lineHeight="1.5">
            {note.note}
          </Text>
        </Box>
      </VStack>
    </Box>
  )
}

function AddNote({ object_model, object_id, dispatchNoteCreated }) {
  const [note, setNote] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const cardBg = "rgba(255, 255, 255, 0.9)";
  const borderColor = "gray.200";
  const toast = useToast();

  const handleCreate = async () => {
    if (!object_model || !object_id || !note?.trim()) {
      if (!object_model || !object_id) {
        toast({
          title: 'Not eklenemedi',
          description: 'Sistem bağlamı eksik (object_model veya object_id). Lütfen sayfayı yenileyin.',
          status: 'error'
        });
      } else {
        console.warn('AddNote: note is empty');
      }
      return;
    }

    setIsLoading(true);
    try {
      await client.post('/notes/', { note, ...{ object_model, object_id } });
      dispatchNoteCreated();
      setNote('');
    } catch (error) {
      console.error('Error creating note:', error);
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <Box
      bg={cardBg}
      backdropFilter="blur(20px)"
      border="1px solid"
      borderColor={borderColor}
      borderRadius="lg"
      p={4}
      mb={4}
    >
      <VStack spacing={3} align="stretch">
        <HStack spacing={2} align="center">
          <Icon as={FiMessageSquare} color="blue.500" boxSize={4} />
          <Text fontSize="sm" fontWeight="semibold" color="blue.600">
            Yeni Not Ekle
          </Text>
        </HStack>

        <Textarea
          placeholder="Notunuzu buraya yazın..."
          value={note}
          onChange={(e) => setNote(e.target.value)}
          bg={'white'}
          border="1px solid"
          borderColor={'gray.200'}
          borderRadius="md"
          resize="vertical"
          minH="80px"
          fontSize="sm"
          _focus={{
            borderColor: "blue.400",
            boxShadow: "0 0 0 1px var(--chakra-colors-blue-400)"
          }}
          onKeyPress={(e) => {
            if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
              e.preventDefault();
              handleCreate();
            }
          }}
        />

        <HStack justify="space-between" align="center">

          <Button
            size="sm"
            colorScheme="blue"
            leftIcon={<Icon as={AddIcon} boxSize={3} />}
            onClick={handleCreate}
            isDisabled={!note?.trim() || !object_model || !object_id}
            isLoading={isLoading}
            loadingText="Ekleniyor..."
            _hover={{
              transform: "translateY(-1px)",
              boxShadow: "md"
            }}
          >
            Not Ekle
          </Button>
        </HStack>
      </VStack>
    </Box>
  )
}

export default function Notes({ object_model, object_id, header, isNaive = false, ...props }) {
  const [notes, setNotes] = useState([]);
  const [noteCreated, dispatchNoteCreated] = useReducer(noteCreatedReducer, 0);
  const [isLoading, setIsLoading] = useState(true);

  // Move useColorModeValue to component level
  const skeletonBg = "gray.50";

  const updateNotes = async () => {
    if (!object_model || !object_id) {
      console.warn('Notes component: object_model or object_id is undefined');
      return;
    }

    setIsLoading(true);
    try {
      const response = await client.get('/notes/', { params: { object_model, object_id } });
      setNotes(response.data);
    } catch (error) {
      console.error('Error fetching notes:', error);
    } finally {
      setIsLoading(false);
    }
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { updateNotes() }, [noteCreated, object_model, object_id]);

  const EmptyState = () => (
    <VStack spacing={4} py={8} align="center">
      <Icon as={FiMessageSquare} boxSize={12} color="gray.400" />
      <VStack spacing={2} textAlign="center">
        <Text fontSize="md" fontWeight="semibold" color="gray.500">
          Henüz not bulunmuyor
        </Text>
        <Text fontSize="sm" color="gray.400">
          İlk notu ekleyerek başlayın
        </Text>
      </VStack>
    </VStack>
  );

  return (
    <Card>
      {header && <CardHeader isNaive={isNaive}><CardTitle isNaive={isNaive}>{header}</CardTitle></CardHeader >}
      <CardContent>
        <VStack spacing={4} align="stretch" {...props}>
          <AddNote {...{ object_model, object_id, dispatchNoteCreated }} />

          {isLoading ? (
            <VStack spacing={3}>
              {[1, 2, 3].map((i) => (
                <Box
                  key={i}
                  bg={skeletonBg}
                  borderRadius="lg"
                  p={4}
                  w="full"
                >
                  <HStack spacing={3} mb={3}>
                    <Box w={8} h={8} bg="gray.300" borderRadius="full" />
                    <VStack spacing={1} align="start" flex={1}>
                      <Box h={3} bg="gray.300" borderRadius="sm" w="120px" />
                      <Box h={2} bg="gray.200" borderRadius="sm" w="80px" />
                    </VStack>
                  </HStack>
                  <Box h={12} bg="gray.200" borderRadius="md" />
                </Box>
              ))}
            </VStack>
          ) : notes.length === 0 ? (
            <EmptyState />
          ) : (
            <VStack spacing={0} align="stretch">
              {notes.map((note, i) => (
                <Note
                  note={note}
                  key={'note-' + urlToId(note.url)}
                  isLast={i + 1 === notes.length}
                  dispatchNoteCreated={dispatchNoteCreated}
                />
              ))}
            </VStack>
          )}
        </VStack>
      </CardContent>
    </Card>
  )
}
